import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";
import { 
  Printer,
  Package,
  Check,
  Minus,
  Plus,
  X,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  Ruler,
  Palette,
  Tag,
  Receipt,
  Download,
  Image as ImageIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProductPhoto from "../components/ProductPhoto";
import { photoStorage } from "../lib/photoStorage";

// Label format configurations
const LABEL_FORMATS = {
  hanging: {
    name: "Étiquette pendante",
    description: "5cm × 8cm portrait - Code-barres uniquement",
    width_mm: 50,
    height_mm: 80,
    width_px: 189,
    height_px: 302,
    cols: 4,
    rows: 3,
    per_page: 12,
    icon: Tag
  },
  small: {
    name: "Petite étiquette prix",
    description: "3cm × 2cm paysage - Code-barres uniquement",
    width_mm: 30,
    height_mm: 20,
    width_px: 113,
    height_px: 76,
    cols: 6,
    rows: 14,
    per_page: 84,
    icon: Receipt
  }
};

export default function Labels() {
  const { getAuthHeaders } = useAuth();
  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [labelFormat, setLabelFormat] = useState("hanging");
  const [productPhotos, setProductPhotos] = useState({});
  const printRef = useRef(null);

  const currentFormat = LABEL_FORMATS[labelFormat];

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, getAuthHeaders());
      setProducts(response.data);
      const photos = await photoStorage.getAllPhotos();
      setProductPhotos(photos);
    } catch (error) {
      toast.error("Erreur lors du chargement des articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getVariantKey = (productId, variantId) => `${productId}__${variantId}`;

  const toggleVariant = (productId, variantId) => {
    const key = getVariantKey(productId, variantId);
    setSelectedVariants(prev => {
      const newSelected = { ...prev };
      if (newSelected[key]) {
        delete newSelected[key];
      } else {
        newSelected[key] = 1;
      }
      return newSelected;
    });
  };

  const updateVariantQuantity = (productId, variantId, delta) => {
    const key = getVariantKey(productId, variantId);
    setSelectedVariants(prev => {
      const current = prev[key] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const newSelected = { ...prev };
        delete newSelected[key];
        return newSelected;
      }
      return { ...prev, [key]: newQty };
    });
  };

  const setVariantQuantity = (productId, variantId, value) => {
    const key = getVariantKey(productId, variantId);
    const qty = parseInt(value) || 0;
    setSelectedVariants(prev => {
      if (qty <= 0) {
        const newSelected = { ...prev };
        delete newSelected[key];
        return newSelected;
      }
      return { ...prev, [key]: qty };
    });
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const selectAllVariants = () => {
    const newSelected = {};
    products.forEach(product => {
      (product.variants || []).forEach(variant => {
        const key = getVariantKey(product.id, variant.id);
        newSelected[key] = 1;
      });
    });
    setSelectedVariants(newSelected);
    const expanded = {};
    products.forEach(p => { expanded[p.id] = true; });
    setExpandedProducts(expanded);
  };

  const clearSelection = () => {
    setSelectedVariants({});
  };

  const getTotalLabels = () => {
    return Object.values(selectedVariants).reduce((sum, qty) => sum + qty, 0);
  };

  const getSelectedVariantsList = () => {
    const list = [];
    Object.entries(selectedVariants).forEach(([key, qty]) => {
      const [productId, variantId] = key.split('__');
      const product = products.find(p => p.id === productId);
      if (product) {
        const variant = (product.variants || []).find(v => v.id === variantId);
        if (variant) {
          for (let i = 0; i < qty; i++) {
            list.push({ product, variant });
          }
        }
      }
    });
    return list;
  };

  const getProductSelectedCount = (productId) => {
    let count = 0;
    Object.entries(selectedVariants).forEach(([key, qty]) => {
      if (key.startsWith(productId + '__')) {
        count += qty;
      }
    });
    return count;
  };

  const handlePrint = () => {
    if (getTotalLabels() === 0) {
      toast.error("Veuillez sélectionner au moins une variante");
      return;
    }
    setPreviewOpen(true);
  };

  const executePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      const printContent = printRef.current;
      if (printContent) {
        const printWindow = window.open('', '_blank');
        const format = LABEL_FORMATS[labelFormat];
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Étiquettes - BoutiqueOS</title>
            <style>
              @page {
                size: A4;
                margin: 5mm;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: 'Arial', sans-serif;
              }
              .labels-grid {
                display: grid;
                grid-template-columns: repeat(${format.cols}, ${format.width_mm}mm);
                gap: 2mm;
                justify-content: center;
              }
              .label-hanging {
                width: ${LABEL_FORMATS.hanging.width_mm}mm;
                height: ${LABEL_FORMATS.hanging.height_mm}mm;
                border: 0.5px dashed #ccc;
                display: flex;
                align-items: center;
                justify-content: center;
                page-break-inside: avoid;
                background: white;
              }
              .label-small {
                width: ${LABEL_FORMATS.small.width_mm}mm;
                height: ${LABEL_FORMATS.small.height_mm}mm;
                border: 0.5px dashed #ccc;
                padding: 1mm;
                display: flex;
                align-items: center;
                justify-content: center;
                page-break-inside: avoid;
                background: white;
              }
              @media print {
                .label-hanging, .label-small {
                  border: 0.5px dashed #ddd;
                }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
          </html>
        `);
        printWindow.document.close();
        
        const barcodeElements = printWindow.document.querySelectorAll('.barcode-svg');
        barcodeElements.forEach(el => {
          const code = el.getAttribute('data-barcode');
          const isSmall = el.getAttribute('data-small') === 'true';
          if (code) {
            try {
              JsBarcode(el, code, {
                format: "EAN13",
                width: isSmall ? 0.8 : 1.2,
                height: isSmall ? 15 : 30,
                displayValue: true,
                fontSize: isSmall ? 6 : 8,
                margin: 0
              });
            } catch (e) {
              console.error('Barcode error:', e);
            }
          }
        });

        setTimeout(() => {
          printWindow.print();
          printWindow.close();
          setPrinting(false);
          setPreviewOpen(false);
          toast.success("Impression lancée");
        }, 500);
      }
    }, 100);
  };

  const buildPrintableClone = async () => {
    const { default: html2canvas } = await import("html2canvas");
    const src = printRef.current;
    if (!src) return null;

    const clone = src.cloneNode(true);
    clone.style.background = "white";
    clone.style.padding = "16px";
    document.body.appendChild(clone);

    const svgs = clone.querySelectorAll(".barcode-svg");
    svgs.forEach((el) => {
      const code = el.getAttribute("data-barcode");
      const isSmall = el.getAttribute("data-small") === "true";
      if (code) {
        try {
          JsBarcode(el, code, {
            format: "EAN13",
            width: isSmall ? 1 : 2,
            height: isSmall ? 30 : 60,
            displayValue: true,
            fontSize: isSmall ? 7 : 12,
            margin: 0,
          });
        } catch (_) {}
      }
    });

    const canvas = await html2canvas(clone, { scale: 2, backgroundColor: "#ffffff" });
    document.body.removeChild(clone);
    return canvas;
  };

  const downloadPNG = async () => {
    setDownloading("png");
    try {
      const canvas = await buildPrintableClone();
      if (!canvas) throw new Error("no canvas");
      const link = document.createElement("a");
      link.download = `etiquettes-${labelFormat}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image PNG téléchargée");
    } catch {
      toast.error("Erreur lors du téléchargement PNG");
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading("pdf");
    try {
      const { default: jsPDF } = await import("jspdf");
      const canvas = await buildPrintableClone();
      if (!canvas) throw new Error("no canvas");
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const usableW = pageW - margin * 2;
      const ratio = canvas.height / canvas.width;
      let imgW = usableW;
      let imgH = imgW * ratio;

      if (imgH > pageH - margin * 2) {
        let yOffset = 0;
        const sliceH = ((pageH - margin * 2) / imgH) * canvas.height;
        let page = 0;
        while (yOffset < canvas.height) {
          if (page > 0) pdf.addPage();
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(sliceH, canvas.height - yOffset);
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);
          const sliceData = sliceCanvas.toDataURL("image/png");
          const sliceRatio = sliceCanvas.height / sliceCanvas.width;
          pdf.addImage(sliceData, "PNG", margin, margin, usableW, usableW * sliceRatio);
          yOffset += sliceH;
          page++;
        }
      } else {
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
      }

      pdf.save(`etiquettes-${labelFormat}-${Date.now()}.pdf`);
      toast.success("Fichier PDF téléchargé");
    } catch {
      toast.error("Erreur lors du téléchargement PDF");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedList = getSelectedVariantsList();
  const totalPages = Math.ceil(selectedList.length / currentFormat.per_page);

  return (
    <div className="space-y-6" data-testid="labels-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Étiquettes</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Générez des étiquettes avec taille, couleur et code-barres
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAllVariants} data-testid="select-all-btn">
            <Check className="w-4 h-4 mr-2" />
            Tout sélectionner
          </Button>
          <Button variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
            <X className="w-4 h-4 mr-2" />
            Effacer
          </Button>
        </div>
      </div>

      {/* Format selection */}
      <Card className="border-primary/30">
        <CardContent className="p-4">
          <Label className="text-sm font-medium mb-3 block">Format d'étiquette</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(LABEL_FORMATS).map(([key, format]) => {
              const Icon = format.icon;
              const isSelected = labelFormat === key;
              return (
                <button
                  key={key}
                  onClick={() => setLabelFormat(key)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`format-${key}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{format.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{format.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format.per_page} étiquettes par page A4
                      </p>
                    </div>
                    {isSelected && (
                      <Check className="w-5 h-5 text-primary shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selection summary */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold">
                {Object.keys(selectedVariants).length} variante(s) sélectionnée(s)
              </p>
              <p className="text-sm text-muted-foreground">
                {getTotalLabels()} étiquette(s) - {totalPages} page(s) A4
              </p>
            </div>
          </div>
          <Button 
            onClick={handlePrint}
            disabled={getTotalLabels() === 0}
            className="btn-primary"
            data-testid="print-labels-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Aperçu
          </Button>
        </CardContent>
      </Card>

      {/* Products list with variants */}
      {products.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Aucun article trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {products.map((product) => {
            const isExpanded = expandedProducts[product.id];
            const selectedCount = getProductSelectedCount(product.id);
            const hasVariants = product.variants && product.variants.length > 0;
            
            return (
              <Card 
                key={product.id} 
                className={`transition-all ${selectedCount > 0 ? 'border-primary/50 bg-primary/5' : ''}`}
                data-testid={`label-product-${product.id}`}
              >
                <Collapsible open={isExpanded} onOpenChange={() => toggleProductExpand(product.id)}>
                  <CollapsibleTrigger asChild>
                    <CardContent className="p-4 cursor-pointer hover:bg-secondary/30">
                      <div className="flex items-center gap-4">
                        <ProductPhoto 
                          productId={product.id}
                          backendImage={product.image}
                          categoryName={product.category_name}
                          categoryIcon={product.category_icon}
                          size="sm"
                        />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium truncate">{product.name}</h3>
                            <Badge variant="secondary" className="text-xs shrink-0">
                              {product.sale_price.toFixed(2)} {devise}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <span>{product.category_name}</span>
                            <span>•</span>
                            <span>{product.gender}</span>
                            <span>•</span>
                            <span>{product.variants?.length || 0} variante(s)</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {selectedCount > 0 && (
                            <Badge className="bg-primary text-primary-foreground">
                              {selectedCount} sélectionnée(s)
                            </Badge>
                          )}
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    {hasVariants ? (
                      <div className="px-4 pb-4 pt-0 border-t">
                        <div className="grid gap-2 mt-3">
                          {product.variants.map((variant) => {
                            const variantKey = getVariantKey(product.id, variant.id);
                            const isSelected = !!selectedVariants[variantKey];
                            const quantity = selectedVariants[variantKey] || 0;
                            
                            return (
                              <div 
                                key={variant.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                  isSelected ? 'border-primary bg-primary/10' : 'border-border'
                                }`}
                                data-testid={`variant-${variant.id}`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleVariant(product.id, variant.id)}
                                />
                                
                                <div 
                                  className="w-5 h-5 rounded-full border-2 shrink-0"
                                  style={{ backgroundColor: variant.color_hex }}
                                />
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      <Ruler className="w-3 h-3 mr-1" />
                                      {variant.size}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                      <Palette className="w-3 h-3 mr-1" />
                                      {variant.color}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Code-barres: {variant.barcode} • Stock: {variant.quantity}
                                  </p>
                                </div>

                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateVariantQuantity(product.id, variant.id, -1);
                                    }}
                                    disabled={!isSelected}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </Button>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={quantity}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setVariantQuantity(product.id, variant.id, e.target.value);
                                    }}
                                    className="w-14 h-7 text-center text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateVariantQuantity(product.id, variant.id, 1);
                                    }}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="px-4 pb-4 pt-0 border-t">
                        <p className="text-sm text-muted-foreground mt-3">
                          Aucune variante pour cet article
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* Print Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="print-preview-dialog">
          <DialogHeader>
            <DialogTitle>Aperçu - {currentFormat.name}</DialogTitle>
            <DialogDescription>
              {getTotalLabels()} étiquette(s) sur {totalPages} page(s) A4 • Format: {currentFormat.width_mm/10}cm × {currentFormat.height_mm/10}cm
            </DialogDescription>
          </DialogHeader>

          {/* Preview content */}
          <div className="border rounded-lg p-4 bg-white overflow-auto" style={{ maxHeight: '60vh', minHeight: '200px' }}>
            <div ref={printRef}>
              <div className="labels-grid" style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${currentFormat.cols}, ${currentFormat.width_px}px)`,
                gap: '8px',
                justifyContent: 'center'
              }}>
                {selectedList.length > 0 ? (
                  selectedList.map((item, index) => (
                    labelFormat === 'hanging' ? (
                      <HangingLabel 
                        key={`${item.variant.id}-${index}`} 
                        product={item.product}
                        variant={item.variant}
                      />
                    ) : (
                      <SmallLabel 
                        key={`${item.variant.id}-${index}`} 
                        product={item.product}
                        variant={item.variant}
                      />
                    )
                  ))
                ) : (
                  <p className="col-span-full text-center text-muted-foreground py-8">
                    Aucune variante sélectionnée
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={downloadPNG}
              disabled={!!downloading}
              data-testid="download-png-btn"
            >
              {downloading === "png" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Préparation...
                </>
              ) : (
                <>
                  <ImageIcon className="w-4 h-4 mr-2" />
                  PNG
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={downloadPDF}
              disabled={!!downloading}
              data-testid="download-pdf-btn"
            >
              {downloading === "pdf" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Préparation...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </>
              )}
            </Button>
            <Button 
              onClick={executePrint} 
              className="btn-primary"
              disabled={printing}
              data-testid="confirm-print-btn"
            >
              {printing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Préparation...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Format 1: Hanging fashion label (5cm x 8cm portrait) - Barcode only
function HangingLabel({ product, variant }) {
  const barcodeRef = useRef(null);
  const format = LABEL_FORMATS.hanging;

  useEffect(() => {
    if (barcodeRef.current && variant.barcode) {
      try {
        JsBarcode(barcodeRef.current, variant.barcode, {
          format: "EAN13",
          width: 2,
          height: 60,
          displayValue: true,
          fontSize: 12,
          margin: 0
        });
      } catch (e) {
        console.error('Barcode error:', e);
      }
    }
  }, [variant.barcode]);

  return (
    <div 
      className="label-hanging"
      style={{
        width: `${format.width_px}px`,
        height: `${format.height_px}px`,
        border: '1px dashed #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        boxSizing: 'border-box',
      }}
    >
      <svg 
        ref={barcodeRef} 
        className="barcode-svg"
        data-barcode={variant.barcode}
        data-small="false"
      />
    </div>
  );
}

// Format 2: Small price label (3cm x 2cm landscape) - Barcode only
function SmallLabel({ product, variant }) {
  const barcodeRef = useRef(null);
  const format = LABEL_FORMATS.small;

  useEffect(() => {
    if (barcodeRef.current && variant.barcode) {
      try {
        JsBarcode(barcodeRef.current, variant.barcode, {
          format: "EAN13",
          width: 1,
          height: 30,
          displayValue: true,
          fontSize: 7,
          margin: 0
        });
      } catch (e) {
        console.error('Barcode error:', e);
      }
    }
  }, [variant.barcode]);

  return (
    <div 
      className="label-small"
      style={{
        width: `${format.width_px}px`,
        height: `${format.height_px}px`,
        border: '1px dashed #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        boxSizing: 'border-box',
        padding: '2px',
      }}
    >
      <svg 
        ref={barcodeRef} 
        className="barcode-svg"
        data-barcode={variant.barcode}
        data-small="true"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}
